// Évaluations — cockpit de campagne : KPIs, heatmap par entité,
// relances des retardataires, activité récente, feed des rappels,
// et la table Équipe. Ported from ev-cockpit.jsx; the demo emails
// become real reminder actions (POST /remind) with a preview modal.
import { useMemo } from 'react';
import { ENTITIES, evalTemplateCrits, evalWeighted, type EvalCycle, type EvalEmployeeDTO, type EvalReminderDTO, type EvalStep, type EvaluationDTO } from '@pointage/shared';
import { CgCount } from '../conges/common.js';
import { EvEmpCell, EvStPill, evFmtYmd, evNum } from './common.js';

export interface EvRow {
  e: EvalEmployeeDTO;
  ev: EvaluationDTO;
}

// ─── heatmap complétion par entité ───────────────────────────
export function EvHeat({ rows, onCell }: { rows: EvRow[]; onCell: (entityId: string) => void }) {
  const cols: Array<[string, string]> = [['auto', 'Auto-éval'], ['mgr', 'Éval mgr'], ['oto', '1:1 calé'], ['done', 'Clôturée']];
  const of = (entityId: string, k: string) => {
    const es = rows.filter((r) => r.e.entityId === entityId);
    if (!es.length) return { n: 0, t: 0, p: 0 };
    const n = es.filter(({ ev }) => {
      if (k === 'auto') return ev.auto.status === 'SUBMITTED';
      if (k === 'mgr') return ev.mgr.status === 'SUBMITTED';
      if (k === 'oto') return !!ev.oto.chosen;
      return ev.final.done;
    }).length;
    return { n, t: es.length, p: Math.round((n / es.length) * 100) };
  };
  return (
    <div className="ev-heat">
      <span />
      {cols.map((c) => <span key={c[0]} className="hc">{c[1]}</span>)}
      {ENTITIES.map((ent) => (
        <div key={ent.id} style={{ display: 'contents' }}>
          <span className="hl">
            <i style={{ width: 8, height: 8, borderRadius: 3, background: ent.color, display: 'inline-block', marginRight: 7 }} />
            {ent.short}
          </span>
          {cols.map((c) => {
            const v = of(ent.id, c[0]);
            return (
              <div key={c[0]} className="ev-heat-cell" style={{ '--p': v.p } as React.CSSProperties} data-hi={v.p > 55 ? 1 : 0}
                title={`${ent.short} · ${c[1]} : ${v.p}%`} onClick={() => onCell(ent.id)}>
                {v.n}/{v.t}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── email preview (relance) ─────────────────────────────────
export function EvEmailModal({ emp, cycle, onClose, onSend, sending }: {
  emp: EvalEmployeeDTO;
  cycle: EvalCycle;
  onClose: () => void;
  onSend: () => void;
  sending?: boolean;
}) {
  const firstName = emp.name.trim().split(/\s+/).at(-1) ?? emp.name;
  return (
    <div className="ev-modal-ov" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ev-modal">
        <div className="ev-modal-h">
          <h3>Aperçu du rappel avant envoi</h3>
          <button className="sb-dr-x" onClick={onClose}>✕</button>
        </div>
        <div className="ev-modal-b">
          <div className="ev-mailbox">
            <div className="ev-mail-chrome"><i /><i /><i /></div>
            <div className="ev-mail-meta">
              <span><b>À :</b> {emp.name} &lt;{emp.email}&gt;</span>
              <span><b>Objet :</b> Rappel — votre auto-évaluation annuelle (échéance {evFmtYmd(cycle.dAuto)})</span>
            </div>
            <div className="ev-mail-body">
              <p>Bonjour {firstName},</p>
              <p>
                Votre <b>auto-évaluation annuelle</b> n'est pas encore soumise. La date limite est le{' '}
                <b>{evFmtYmd(cycle.dAuto)}</b> — comptez environ 30 minutes pour la compléter. Votre entretien 1:1
                ne pourra être planifié qu'une fois les deux évaluations soumises.
              </p>
              <span className="cta">Compléter mon auto-évaluation →</span>
              <p className="sig">Rappel envoyé via Pointage · {cycle.lbl}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 9, justifyContent: 'flex-end', marginTop: 14 }}>
            <button className="cg-ghost" onClick={onClose}>Annuler</button>
            <button className="gm-attn-go" disabled={sending} onClick={onSend}>✉ Envoyer le rappel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── vue Campagne ────────────────────────────────────────────
const sentToday = (ev: EvaluationDTO, today: string) => !!ev.remindedAt && ev.remindedAt.slice(0, 10) === today;

export function EvCampagne({ rows, cycle, today, reminders, daysLeft, onOpen, onRemind, onRemindAll, onCell }: {
  rows: EvRow[];
  cycle: EvalCycle;
  today: string;
  reminders: EvalReminderDTO[];
  daysLeft: number;
  onOpen: (emp: EvalEmployeeDTO, tab: string) => void;
  onRemind: (emp: EvalEmployeeDTO) => void;
  onRemindAll: (emps: EvalEmployeeDTO[]) => void;
  onCell: (entityId: string) => void;
}) {
  const nAuto = rows.filter(({ ev }) => ev.auto.status === 'SUBMITTED').length;
  const nMgr = rows.filter(({ ev }) => ev.mgr.status === 'SUBMITTED').length;
  const nOto = rows.filter(({ ev }) => ev.oto.chosen && !ev.final.done).length;
  const nDone = rows.filter(({ ev }) => ev.final.done).length;
  const late = rows.filter(({ ev }) => ev.auto.status !== 'SUBMITTED');
  const lateUnsent = late.filter(({ ev, e }) => !sentToday(ev, today) && e.email);

  const feed = useMemo(() => {
    const out: Array<{ dt: string; t: string; ic: string; c: string; msg: React.ReactNode }> = [];
    for (const { e, ev } of rows) {
      if (ev.auto.submittedAt) out.push({ dt: ev.auto.submittedAt, t: evFmtYmd(ev.auto.submittedAt).slice(0, 5), ic: '📥', c: 'var(--shfs)', msg: <p><b>{e.name}</b> a soumis son auto-évaluation</p> });
      if (ev.mgr.submittedAt) out.push({ dt: ev.mgr.submittedAt, t: evFmtYmd(ev.mgr.submittedAt).slice(0, 5), ic: '📝', c: 'var(--infos)', msg: <p>Évaluation manager soumise pour <b>{e.name}</b></p> });
      if (ev.oto.chosen) out.push({ dt: ev.oto.chosen.d, t: evFmtYmd(ev.oto.chosen.d).slice(0, 5), ic: '🗓', c: 'var(--infos)', msg: <p>1:1 confirmé avec <b>{e.name}</b> ({ev.oto.chosen.h})</p> });
      if (ev.final.done && ev.final.signedMgrAt) out.push({ dt: ev.final.signedMgrAt, t: evFmtYmd(ev.final.signedMgrAt).slice(0, 5), ic: '✍️', c: 'var(--greens)', msg: <p>Fiche de <b>{e.name}</b> clôturée et signée</p> });
    }
    return out.sort((a, b) => (b.dt < a.dt ? -1 : 1));
  }, [rows]);

  const KPIS: Array<{ v: number; t?: number; l: string; cls?: string }> = [
    { v: nAuto, t: rows.length, l: 'Auto-évaluations soumises', cls: 'accent' },
    { v: nMgr, t: rows.length, l: 'Évaluations manager' },
    { v: nOto, l: 'Entretiens 1:1 planifiés', cls: 'info' },
    { v: nDone, l: 'Fiches clôturées & signées' },
  ];

  return (
    <div className="view-enter">
      <div className="cg-kpis">
        {KPIS.map((k, i) => (
          <div key={i} className="cg-kpi">
            <span className={'cg-kpi-v ' + (k.cls ?? '')}>
              <CgCount value={k.v} dec={0} />{k.t != null && <i>/{k.t}</i>}
            </span>
            <span className="cg-kpi-l">{k.l}</span>
          </div>
        ))}
      </div>
      <div className="ev-cols">
        <div style={{ display: 'grid', gap: 14 }}>
          <div className="ev-card">
            <div className="ev-card-h">
              <h3>Complétion par entité</h3><span className="k">4 étapes</span><span className="sp" />
              <span style={{ fontSize: 11, color: 'var(--ink4)', fontWeight: 600 }}>cliquez pour filtrer l'équipe</span>
            </div>
            <EvHeat rows={rows} onCell={onCell} />
          </div>
          <div className="ev-card">
            <div className="ev-card-h">
              <h3>Auto-évaluations en retard</h3><span className="k">{late.length}</span><span className="sp" />
              <span className={'ev-due ' + (daysLeft <= 3 ? 'bad' : 'warn')}>
                échéance {evFmtYmd(cycle.dAuto)}{daysLeft >= 0 ? ` · J-${daysLeft}` : ''}
              </span>
              {lateUnsent.length > 0 && (
                <button className="ev-relance" onClick={() => onRemindAll(lateUnsent.map((x) => x.e))}>
                  ✉ Relancer tous ({lateUnsent.length})
                </button>
              )}
            </div>
            <div className="ev-late">
              {late.map(({ e, ev }, i) => (
                <div key={e.id} className="ev-late-row" style={{ animationDelay: `${i * 0.03}s` }}>
                  <EvEmpCell emp={e} />
                  <span className="who" />
                  {ev.auto.status === 'DRAFT'
                    ? <span className="ev-st" style={{ color: 'var(--warnc)', background: 'var(--warns)' }}><i />Brouillon {ev.auto.pct}%</span>
                    : <span className="ev-st" style={{ color: 'var(--badc)', background: 'var(--bads)' }}><i />Pas commencée</span>}
                  {sentToday(ev, today)
                    ? <button className="ev-relance sent">✓ Relancé aujourd'hui</button>
                    : e.email
                      ? <button className="ev-relance" onClick={() => onRemind(e)}>✉ Relancer</button>
                      : <span className="ev-due" style={{ color: 'var(--ink4)', background: 'var(--s2)' }} title="Aucune adresse email sur la fiche">pas d'email</span>}
                  <button className="ev-relance" onClick={() => onOpen(e, 'eval')}>Ouvrir ›</button>
                </div>
              ))}
              {late.length === 0 && <div className="ev-empty">Toutes les auto-évaluations sont soumises 🎉</div>}
            </div>
          </div>
        </div>
        <div style={{ display: 'grid', gap: 14, alignContent: 'start' }}>
          <div className="ev-card">
            <div className="ev-card-h"><h3>Activité récente</h3></div>
            <div className="ev-feed">
              {feed.slice(0, 7).map((f, i) => (
                <div key={i} className="ev-feed-row">
                  <span className="t">{f.t}</span>
                  <span className="ic" style={{ background: f.c }}>{f.ic}</span>
                  {f.msg}
                </div>
              ))}
              {feed.length === 0 && <div className="ev-empty">Aucune activité pour l'instant — la campagne démarre.</div>}
            </div>
          </div>
          <div className="ev-card">
            <div className="ev-card-h"><h3>Rappels envoyés</h3><span className="k">{reminders.length}</span></div>
            <div className="ev-feed">
              {reminders.map((r, i) => (
                <div key={i} className="ev-feed-row">
                  <span className="t">{evFmtYmd(r.at.slice(0, 10)).slice(0, 5)}</span>
                  <span className="ic" style={{ background: 'var(--shfs)' }}>✉️</span>
                  <p>
                    <b>Rappel auto-évaluation</b> → {r.to}<br />
                    <span style={{ fontSize: 11, color: 'var(--ink4)' }}>par {r.byName ?? '—'} · {r.at.slice(11, 16)}</span>
                  </p>
                </div>
              ))}
              {reminders.length === 0 && <div className="ev-empty">Aucun rappel envoyé pour cette campagne.</div>}
            </div>
            <div className="ev-gap-note" style={{ margin: '0 16px 14px' }}>
              ✉️ Chaque relance part par email (Gmail) et est tracée dans le journal d'activité.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── vue Équipe ──────────────────────────────────────────────
const STEP_ORDER: Record<EvalStep, number> = { none: 0, draft: 1, partial: 2, gap: 3, oto: 4, done: 5 };

export function EvEquipe({ rows, filter, onFilter, onOpen }: {
  rows: EvRow[];
  filter: string | null;
  onFilter: (f: string | null) => void;
  onOpen: (emp: EvalEmployeeDTO, tab: string) => void;
}) {
  const shown = rows
    .filter(({ e }) => !filter || e.entityId === filter)
    .sort((a, b) => STEP_ORDER[a.ev.step] - STEP_ORDER[b.ev.step]);
  const entShort = filter ? ENTITIES.find((x) => x.id === filter)?.short ?? filter : null;
  return (
    <div className="view-enter">
      {filter && (
        <div style={{ marginBottom: 12 }}>
          <button className="cg-ghost" onClick={() => onFilter(null)}>✕ Filtre : {entShort}</button>
        </div>
      )}
      <div className="gm-gridwrap">
        <div className="gm-scroll">
          <table className="sb-tbl">
            <thead>
              <tr><th>Employé</th><th>Étape</th><th className="num">Auto-éval</th><th className="num">Manager</th><th className="num">Écart</th><th className="num">Finale</th><th>1:1</th><th /></tr>
            </thead>
            <tbody>
              {shown.map(({ e, ev }, i) => {
                const crits = evalTemplateCrits(ev.template);
                const Wa = ev.auto.status === 'SUBMITTED' && ev.auto.notes ? evalWeighted(ev.auto.notes, crits) : null;
                const Wm = ev.mgr.status === 'SUBMITTED' ? evalWeighted(ev.mgr.notes, crits) : null;
                const d = Wa && Wm ? Wm.note - Wa.note : null;
                const tab = ev.step === 'gap' ? 'gap' : ev.step === 'oto' || ev.step === 'done' ? 'oto' : 'eval';
                return (
                  <tr key={e.id} className="cg-row" style={{ animationDelay: `${i * 0.02}s` }} onClick={() => onOpen(e, tab)}>
                    <td><EvEmpCell emp={e} /></td>
                    <td><EvStPill step={ev.step} /></td>
                    <td className="num">
                      <span className="ev-note-big" style={{ color: Wa ? 'var(--shf)' : 'var(--ink4)' }}>
                        {Wa ? evNum(Wa.note) : ev.auto.status === 'DRAFT' ? `${ev.auto.pct}%` : '—'}
                      </span>
                    </td>
                    <td className="num">
                      <span className="ev-note-big" style={{ color: Wm ? 'var(--infoc)' : 'var(--ink4)' }}>
                        {Wm ? evNum(Wm.note) : ev.mgr.status === 'DRAFT' ? '…' : '—'}
                      </span>
                    </td>
                    <td className="num">
                      {d != null
                        ? <span className={'ev-delta ' + (d > 0.05 ? 'pos' : d < -0.05 ? 'neg' : 'zero')}>{d > 0 ? '+' : ''}{evNum(d)}</span>
                        : <span style={{ color: 'var(--ink4)' }}>—</span>}
                    </td>
                    <td className="num">
                      <span className="ev-note-big" style={{ color: ev.final.done ? 'var(--greenc)' : 'var(--ink4)' }}>
                        {ev.final.done ? evNum(evalWeighted(ev.final.notes, crits).note) : '—'}
                      </span>
                    </td>
                    <td style={{ fontSize: 11.5, fontWeight: 600, color: ev.oto.chosen ? 'var(--ink)' : 'var(--ink4)' }}>
                      {ev.oto.chosen
                        ? `${evFmtYmd(ev.oto.chosen.d).slice(0, 5)} · ${ev.oto.chosen.h}`
                        : ev.oto.stage === 'PROPOSED' ? `${ev.oto.slots.length} créneaux proposés` : '—'}
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--ink4)' }}>›</td>
                  </tr>
                );
              })}
              {shown.length === 0 && (
                <tr><td colSpan={8}><div className="cg-empty" style={{ margin: 14 }}>Aucun employé pour ce filtre.</div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
